import PaginatedTableV2, { PaginatedTableProps } from '../PaginatedTableV2';
import React, { ReactNode } from 'react';
import { cj, cm } from 'src/utils/classNames';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { TableStateProps } from 'src/components/TableStateWrapper';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { DataTableProps } from 'src/components/primitives/Table/types';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import ContentGuard from 'src/components/ContentGuard';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import { TABLE_DEFAULT_EMPTY_MESSAGE } from 'src/components/primitives/Table/consts';

// Once we're done migrating history tables to the new table system we should
// delete this (and anything else in the component that's no longer necessary).
interface LegacyProps extends Omit<PaginatedTableProps, 'tableState'> {
	tableState: Omit<TableStateProps, 'emptyStateText' | 'loading'>;
	customMiddleContentWrapper?: boolean;
	loading: boolean;
}

interface NewProps<T> {
	dataTableProps: DataTableProps<T>;
	mobileCard?: ({ record }: { record: T }) => ReactNode;
	isLoading?: boolean;
	contentGuardClassName?: string;
	requireAccount?: boolean;
}

type Props<T> = LegacyProps | NewProps<T>;

const DEFAULT_RECORDS: [] = [];

function isNewProps<T>(props: Props<T>): props is NewProps<T> {
	return 'dataTableProps' in props;
}

export default function HistoryTable<T>(props: Props<T>) {
	const isMobileScreenSize = useIsMobileScreenSize();
	const isWalletConnected = useWalletIsConnected();
	const currentAccountExists = useVelocityAccountStore(
		(s) => !!s.getCurrentUserAccount()
	);

	if (isNewProps(props)) {
		const { mobileCard, dataTableProps, isLoading } = props;

		const content = (() => {
			if (isMobileScreenSize && mobileCard) {
				return (
					<div
						className={cm(MOBILE_TABLE_CARD_LIST_CLASSNAME, 'size-full px-0')}
					>
						{dataTableProps.data.length > 0 ? (
							dataTableProps.data.map((record: T) => mobileCard({ record }))
						) : (
							<MobileTableCard.EmptyPlaceholder>
								{dataTableProps.emptyMessage ?? TABLE_DEFAULT_EMPTY_MESSAGE}
							</MobileTableCard.EmptyPlaceholder>
						)}
					</div>
				);
			}

			const { emptyClassName, ...restDataTableProps } = dataTableProps;

			return (
				<DataTable
					emptyClassName={cj('h-80', emptyClassName)}
					{...restDataTableProps}
				/>
			);
		})();

		return (
			<ContentGuard
				className={cj(
					'sm:rounded-b-sm sm:bg-container-bg',
					props.contentGuardClassName
				)}
				isLoading={isLoading}
				requireAccount={props.requireAccount ?? true}
				requireWallet
			>
				{content}
			</ContentGuard>
		);
	}

	const {
		tableGrid,
		pageSize,
		currentPage,
		tableState,
		totalItems,
		loading,
		pageChangeCallback,
		tableContentTop,
		tableContentMiddle,
		tableContentBottom,
		setIsScrollable,
		customMiddleContentWrapper,
	} = props;

	const { records } = tableState;

	const middleContent = (() => {
		if (customMiddleContentWrapper) {
			return tableContentMiddle;
		}

		return (
			<div className={cm(MOBILE_TABLE_CARD_LIST_CLASSNAME, 'size-full px-0')}>
				{tableContentMiddle}
			</div>
		);
	})();

	return (
		<PaginatedTableV2
			tableGrid={tableGrid}
			pageSize={pageSize}
			currentPage={currentPage}
			pageChangeCallback={pageChangeCallback}
			totalItems={totalItems}
			tableState={{
				...tableState,
				records: records ?? DEFAULT_RECORDS,
				emptyStateText: (
					<div className="flex items-center justify-center">
						No records matching the selected filters
					</div>
				),
				pageChangeLoading: loading,
				loading: loading,
				requireAccountCreated: true,
				requireWalletConnect: true,
				className: 'sm:bg-container-bg rounded-b-sm overflow-hidden',
				innerClassName: cj(
					// Center the loading bar & no results message.
					(!isWalletConnected ||
						!currentAccountExists ||
						loading ||
						records?.length === 0) &&
						'flex min-h-40 items-center justify-center sm:min-h-80'
				),
			}}
			tableContentTop={tableContentTop}
			tableContentMiddle={middleContent}
			tableContentBottom={tableContentBottom}
			setIsScrollable={setIsScrollable}
			disableRateLimitNotification
			isTokenBasedPagination
			hideHeadersOnMobile
			fillSpace
			noBorder
		/>
	);
}
